import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';

import {
  FEATURE_SURFACE_LABELS,
  getFeatureEntry,
  getFeatureLabel,
  sortSectionKeys,
  useFeaturesCatalog,
} from './feature-catalog';

const GENERAL_SECTION = '__general__';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const setIn = (object, path, value) => {
  if (path.length === 0) return value;

  const [key, ...rest] = path;

  return { ...object, [key]: setIn(object?.[key], rest, value) };
};

export const countEnabled = (value) => {
  if (value === true) return 1;
  if (!isPlainObject(value)) return 0;

  return Object.values(value).reduce(
    (sum, child) => sum + countEnabled(child),
    0
  );
};

const normalize = (text) =>
  (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Une option correspond à la recherche si son libellé, son explication ou
 * son chemin technique contient le texte cherché.
 */
const isSelfMatching = (catalog, path, query) => {
  const entry = getFeatureEntry(catalog, path);

  return [getFeatureLabel(catalog, path), entry?.description, path.join('.')]
    .map(normalize)
    .some((text) => text.includes(query));
};

const hasMatch = (catalog, path, value, query) =>
  isSelfMatching(catalog, path, query) ||
  (isPlainObject(value) &&
    Object.entries(value).some(([key, child]) =>
      hasMatch(catalog, [...path, key], child, query)
    ));

const FeatureHelp = ({ entry }) => {
  if (!entry?.description && !entry?.unused) return null;

  return (
    <span className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-1.5 tw-gap-y-0.5 tw-pl-6 tw-text-[11px] tw-leading-[15px] tw-text-[#76808c]">
      {entry.unused && (
        <span className="tw-rounded-[3px] tw-bg-amber-50 tw-px-1 tw-font-medium tw-text-amber-800">
          Sans effet
        </span>
      )}
      {entry.surfaces?.map((surface) => (
        <span
          key={surface}
          className="tw-rounded-[3px] tw-bg-[rgba(118,128,140,0.1)] tw-px-1 tw-font-medium tw-text-[#4a5360]"
        >
          {FEATURE_SURFACE_LABELS[surface] || surface}
        </span>
      ))}
      {entry.description && <span>{entry.description}</span>}
    </span>
  );
};

const Checkbox = ({ checked, onChange, label, entry, isMain }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-py-1">
    <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="tw-size-4 tw-cursor-pointer tw-accent-main"
      />
      <span
        className={`tw-text-[13px] ${
          isMain
            ? 'tw-font-medium tw-text-text-dark-high-importance'
            : 'tw-text-black'
        }`}
      >
        {label}
      </span>
    </label>
    <FeatureHelp entry={entry} />
  </div>
);

const InlineField = ({ label, entry, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-py-1">
    <label className="tw-flex tw-items-center tw-gap-3">
      <span className="tw-w-48 tw-shrink-0 tw-pl-6 tw-text-[13px] tw-text-black">
        {label}
      </span>
      {children}
    </label>
    <FeatureHelp entry={entry} />
  </div>
);

const inputClassName =
  'tw-h-8 tw-w-full tw-max-w-[420px] tw-rounded-[5px] tw-border tw-border-solid tw-border-input-border tw-bg-white tw-px-2.5 tw-text-[13px] focus:tw-border-input-borderFocused focus:tw-outline-none';

/**
 * Rend une valeur de configuration : case à cocher pour un booléen, champ
 * pour un texte / nombre, et récursion pour un objet. Un objet porteur d'un
 * `enabled` est affiché comme une option dont les réglages ne s'ouvrent
 * qu'une fois cochée (ou quand un réglage correspond à la recherche).
 */
const FeatureNode = ({ value, path, onChange, query }) => {
  const catalog = useFeaturesCatalog();
  const label = getFeatureLabel(catalog, path);
  const entry = getFeatureEntry(catalog, path);
  const update = (nextValue) => onChange(path, nextValue);

  if (query && !hasMatch(catalog, path, value, query)) return null;

  if (typeof value === 'boolean') {
    return (
      <Checkbox checked={value} onChange={update} label={label} entry={entry} />
    );
  }

  if (typeof value === 'string') {
    return (
      <InlineField label={label} entry={entry}>
        <input
          className={inputClassName}
          value={value}
          onChange={(event) => update(event.target.value)}
        />
      </InlineField>
    );
  }

  if (typeof value === 'number') {
    return (
      <InlineField label={label} entry={entry}>
        <input
          type="number"
          className={`${inputClassName} tw-max-w-[120px]`}
          value={value}
          onChange={(event) => update(Number(event.target.value))}
        />
      </InlineField>
    );
  }

  if (Array.isArray(value)) {
    const isStringList = value.every((item) => typeof item === 'string');

    return (
      <InlineField label={label} entry={entry}>
        {isStringList ? (
          <input
            className={inputClassName}
            value={value.join(', ')}
            placeholder="Valeurs séparées par des virgules"
            onChange={(event) =>
              update(
                event.target.value
                  .split(',')
                  .map((item) => item.trim())
                  .filter(Boolean)
              )
            }
          />
        ) : (
          <span className="tw-text-[11px] tw-text-[#76808c]">
            {value.length} élément(s) · modifiable dans l’éditeur JSON
          </span>
        )}
      </InlineField>
    );
  }

  if (!isPlainObject(value)) return null;

  const hasToggle = typeof value.enabled === 'boolean';
  const childKeys = Object.keys(value).filter(
    (key) => !(hasToggle && key === 'enabled')
  );
  // Pendant une recherche, une option correspondant elle-même s'affiche en
  // entier ; sinon seuls ses réglages correspondants restent visibles.
  const childQuery =
    query && !isSelfMatching(catalog, path, query) ? query : '';
  const isOpen = !hasToggle || value.enabled || Boolean(childQuery);

  return (
    <div>
      {hasToggle ? (
        <Checkbox
          checked={value.enabled}
          onChange={(checked) => onChange([...path, 'enabled'], checked)}
          label={label}
          entry={entry}
          isMain
        />
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-pb-0.5 tw-pt-1.5">
          <span className="tw-text-[13px] tw-font-medium tw-text-text-dark-high-importance">
            {label}
          </span>
          <FeatureHelp entry={entry} />
        </div>
      )}
      {isOpen && childKeys.length > 0 && (
        <div className="tw-ml-[7px] tw-border-0 tw-border-l tw-border-solid tw-border-[#eeeff2] tw-pl-4">
          {childKeys.map((key) => (
            <FeatureNode
              key={key}
              value={value[key]}
              path={[...path, key]}
              onChange={onChange}
              query={childQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FeatureSection = ({
  title,
  description,
  enabledCount,
  isForcedOpen,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isExpanded = isOpen || isForcedOpen;

  return (
    <div className="tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] last:tw-border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        disabled={isForcedOpen}
        className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-0 tw-bg-white tw-px-4 tw-py-3 tw-text-left hover:tw-bg-[rgba(118,128,140,0.04)] disabled:tw-cursor-default"
      >
        <FontAwesomeIcon
          icon={isExpanded ? faChevronDown : faChevronRight}
          className="tw-size-3 tw-text-[#76808c]"
        />
        <span className="tw-flex tw-flex-1 tw-flex-col">
          <span className="tw-text-[13px] tw-font-semibold tw-text-text-dark-high-importance">
            {title}
          </span>
          {description && (
            <span className="tw-text-[11px] tw-text-[#76808c]">
              {description}
            </span>
          )}
        </span>
        <span className="tw-rounded-full tw-bg-[rgba(7,153,146,0.08)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-main-active">
          {enabledCount} activée{enabledCount > 1 ? 's' : ''}
        </span>
      </button>
      {isExpanded && <div className="tw-px-4 tw-pb-3 tw-pl-10">{children}</div>}
    </div>
  );
};

/**
 * Options regroupées par section de premier niveau. Avec une recherche, seules
 * les sections et options correspondantes s'affichent, dépliées.
 */
export const FeatureTree = ({ value, onChange, search = '' }) => {
  const catalog = useFeaturesCatalog();
  const query = normalize(search.trim());

  // Les clés de premier niveau qui ne sont pas des objets sont regroupées
  // dans une section « Général ».
  const sections = useMemo(() => {
    const entries = sortSectionKeys(catalog, Object.keys(value || {})).map(
      (key) => [key, value[key]]
    );
    const general = entries.filter(([, child]) => !isPlainObject(child));
    const objects = entries.filter(([, child]) => isPlainObject(child));

    return [
      ...(general.length > 0
        ? [{ key: GENERAL_SECTION, title: 'Général', entries: general }]
        : []),
      ...objects.map(([key, child]) =>
        typeof child.enabled === 'boolean'
          ? {
              key,
              title: getFeatureLabel(catalog, [key]),
              entries: [[key, child]],
            }
          : {
              key,
              title: getFeatureLabel(catalog, [key]),
              description: getFeatureEntry(catalog, [key])?.description,
              entries: Object.entries(child),
              path: [key],
            }
      ),
    ];
  }, [value, catalog]);

  const visibleSections = query
    ? sections.filter((section) =>
        section.entries.some(([key, child]) =>
          hasMatch(catalog, [...(section.path || []), key], child, query)
        )
      )
    : sections;

  const handleChange = (path, nextValue) =>
    onChange(setIn(value, path, nextValue));

  if (visibleSections.length === 0) {
    return (
      <p className="tw-m-0 tw-rounded-[6px] tw-border tw-border-dashed tw-border-[#d0d4da] tw-px-4 tw-py-6 tw-text-center tw-text-[13px] tw-text-[#76808c]">
        Aucune option ne correspond à « {search.trim()} ».
      </p>
    );
  }

  return (
    <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
      {visibleSections.map((section) => (
        <FeatureSection
          key={section.key}
          title={section.title}
          description={section.description}
          isForcedOpen={Boolean(query)}
          enabledCount={section.entries.reduce(
            (sum, [, child]) => sum + countEnabled(child),
            0
          )}
        >
          {section.entries.map(([key, child]) => (
            <FeatureNode
              key={key}
              value={child}
              path={[...(section.path || []), key]}
              onChange={handleChange}
              query={query}
            />
          ))}
        </FeatureSection>
      ))}
    </div>
  );
};

export const JsonEditor = ({ value, onChange }) => {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState(null);
  const [emittedValue, setEmittedValue] = useState(value);

  // Resynchronise le texte quand la valeur change hors de l'éditeur (copie
  // d'un client, valeurs par défaut). Une valeur émise par la saisie elle-même
  // est ignorée, sinon le texte serait reformaté à chaque frappe.
  if (value !== emittedValue) {
    setEmittedValue(value);
    setText(JSON.stringify(value, null, 2));
    setError(null);
  }

  const handleChange = (nextText) => {
    setText(nextText);

    try {
      const parsed = JSON.parse(nextText);

      if (!isPlainObject(parsed)) {
        setError('La configuration doit être un objet JSON.');
        return;
      }

      setError(null);
      setEmittedValue(parsed);
      onChange(parsed);
    } catch (parseError) {
      setError(`JSON invalide : ${parseError.message}`);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <textarea
        value={text}
        onChange={(event) => handleChange(event.target.value)}
        spellCheck={false}
        className={`tw-h-[420px] tw-w-full tw-resize-y tw-rounded-[5px] tw-border tw-border-solid tw-bg-white tw-p-3 tw-font-mono tw-text-[12px] focus:tw-outline-none ${
          error ? 'tw-border-input-required' : 'tw-border-input-border'
        }`}
      />
      {error && (
        <div className="tw-text-[11px] tw-text-input-required">{error}</div>
      )}
    </div>
  );
};
